import { Container, Typography } from '@mui/material';
import { LocationDescriptor } from 'history';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, Route, Router, Switch } from 'react-router-dom';
import { GuardedRoute, GuardFunction, GuardProvider } from 'react-router-guards';

import './app.scss';
import { LanguagePicker } from 'app/components/language-picker';
import { NotFound } from 'app/components/not-found';
import { Translate } from 'app/components/translate';
import { Role } from 'app/constants/role';
import { history } from 'app/history';
import { RootState } from 'app/store';
import { RoleAccessUtils } from 'app/utils/role-access-utils';

import { selectLoggedIn, selectRoles } from 'features/auth/auth.slice';
import { Login } from 'features/auth/login';
import { AppOverlays } from 'features/core/app-overlays';
import { CrisRouter } from 'features/cris/cris-router';
import { CrowdSliceRouter } from 'features/crowd/crowd-slice-router';
import { Explore } from 'features/explore/explore';
import { Header } from 'features/layout/header';
import { MessageSnack } from 'features/messaging/message-snack';
import { MlVersions } from 'features/ml-version/ml-versions';
import { QaRouter } from 'features/qa/qa-router';
import { Seam } from 'features/seam/seam';
import { ToolsRouter } from 'features/tools/tools-router';
import { Users } from 'features/user/users';

interface AppProps {
    loggedIn: boolean;
    roles: Record<string, boolean>;
}

interface AppState {
    returnTarget?: LocationDescriptor;
}

class App extends React.Component<AppProps, AppState> {
    public constructor(props: AppProps | Readonly<AppProps>) {
        super(props);

        this.state = {};
    }

    public requireLoginGuard(): GuardFunction {
        return (to, from, next) => {
            if (!to.meta.auth || this.props.loggedIn) {
                next();

                return;
            }

            this.setState({
                returnTarget: to.location,
            });

            next.redirect('/login');
        };
    }

    public requireRoleGuard(): GuardFunction {
        return (to, from, next) => {
            if (!to.meta.role) {
                next();

                return;
            }

            if (!this.props.loggedIn) {
                this.setState({
                    returnTarget: to.location,
                });

                next.redirect('/login');

                return;
            }

            if (RoleAccessUtils.hasAnyRole(this.props.roles, to.meta.role as string)) {
                next();

                return;
            }

            next.redirect('/missing-role');
        };
    }

    public render() {
        return (
            <Router history={history}>
                <GuardProvider guards={[this.requireLoginGuard(), this.requireRoleGuard()]}>
                    <div className='App'>
                        <AppOverlays />
                        <Header />
                        <MessageSnack />
                        <Switch>
                            <Route path='/' exact>
                                <Container>
                                    <h2>Status: Beta</h2>
                                    <h3>
                                        <Translate tkey='things that should work' />:
                                    </h3>
                                    <ul>
                                        <li>
                                            Login/Logout
                                            <ul>
                                                <li>DUO Authentication</li>
                                            </ul>
                                        </li>
                                        <li>Everything that's part of Top Right Menu</li>
                                        <li>
                                            <Link to='/users'>Users</Link> (Create/Read/Update/Delete [CRUD])
                                        </li>
                                        <li>Breadcrumb logic</li>
                                        <li>Rudimentary Error Display</li>
                                        <li>
                                            SEAM Top Menu
                                            <ul>
                                                <li>Kanban Board</li>
                                                <li>Request Creation</li>
                                                <li>Pattern Creation</li>
                                                <li>Pattern Modification</li>
                                                <li>Manager Approval</li>
                                                <li>MID Tuple Lookup (POC)</li>
                                                <li>Request Assignments</li>
                                                <li>Topline Metrics</li>
                                            </ul>
                                        </li>
                                        <li>
                                            CRIS Top Menu
                                            <ul>
                                                <li>Kanban Board</li>
                                                <li>Request Creation</li>
                                                <li>Taxonomy</li>
                                                <li>Brand / Store / Partner search</li>
                                                <li>Brand Attribute Management</li>
                                                <li>Partner Linkage</li>
                                            </ul>
                                        </li>
                                        <li>
                                            Explore Top Menu
                                            <ul>
                                                <li>Seam request search</li>
                                                <li>Tuple Explorer</li>
                                                <li>Pattern Explorer</li>
                                            </ul>
                                        </li>
                                        <li>
                                            Background Tasks:
                                            <ul>
                                                <li>Pattern Transfer to Pre-Prod</li>
                                                <li>Unload changed tuples to S3</li>
                                                <li>Load MID Tuples from S3 to ElasticSearch</li>
                                                <li>Multi pattern match accumulator</li>
                                                <li>Expired Token cleanup</li>
                                            </ul>
                                        </li>
                                    </ul>
                                    <h3>Actively WIP:</h3>
                                    <ul>
                                        <li>QA flow</li>
                                    </ul>
                                    <Typography variant='h4'>Language Test</Typography>
                                    <LanguagePicker />
                                </Container>
                            </Route>
                            <Route path='/login'>
                                <Login returnUrl={this.state.returnTarget} />
                            </Route>
                            <Route path='/tools'>
                                <ToolsRouter />
                            </Route>
                            <GuardedRoute path='/seam' meta={{ role: Role.SEAM_REQUESTS }}>
                                <Seam />
                            </GuardedRoute>
                            <Route path='/cris'>
                                <CrisRouter />
                            </Route>
                            <GuardedRoute path='/qa' meta={{ role: Role.QA_MANAGER }} component={QaRouter} />
                            <GuardedRoute path='/explore' meta={{ role: 'ROLE_USER' }}>
                                <Explore />
                            </GuardedRoute>
                            <GuardedRoute path='/crowd' meta={{ role: 'ROLE_USER' }}>
                                <CrowdSliceRouter />
                            </GuardedRoute>
                            <GuardedRoute path='/users' meta={{ role: 'ROLE_ADMIN' }}>
                                <Users />
                            </GuardedRoute>
                            <GuardedRoute path='/ml-versions' meta={{ role: 'ROLE_ADMIN' }}>
                                <MlVersions />
                            </GuardedRoute>
                            <Route path='/missing-role'>
                                <Container>
                                    <p>You do not have sufficient privileges to access this page.</p>
                                </Container>
                            </Route>
                            <Route path='/'>
                                <NotFound />
                            </Route>
                        </Switch>
                    </div>
                </GuardProvider>
            </Router>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    loggedIn: selectLoggedIn(state),
    roles: selectRoles(state),
});

const connected = connect(mapStateToProps)(
    // for now just preload all i18n namespaces on load to avoid screen flicker
    // once we have lots more translations we can probably leave this out and make loader suspense better instead
    withTranslation(['headings', 'forms', 'translation'])(App),
);

export { connected as App };
